<template>
  <div class="settle-page">
    <div class="top-bar">
      <div class="top-left">
        <a-button size="small" icon="arrow-left" @click="backBoard">返回</a-button>
        <span class="top-title">餐饮结算</span>
        <template v-if="order">
          <span class="top-order-no">{{ order.order_no }}</span>
          <a-tag :color="statusColor(order.status)">{{ order.statusText || statusText(order.status) }}</a-tag>
        </template>
      </div>
      <div class="top-right">
        <span v-if="storeName" class="top-store">{{ storeName }}</span>
        <span class="top-user">{{ userName }}</span>
      </div>
    </div>
    <div class="settle-body" v-if="order">
      <!-- 左：订单摘要（明细含选项，金额全部后端口径） -->
      <div class="summary-side">
        <div class="sum-head">
          <span class="sum-table">{{ tableLabel }}</span>
          <span class="sum-no">{{ order.order_no }}</span>
        </div>
        <div class="sum-meta">
          <span>{{ order.order_type === 'dine_in' ? '堂食' : '外带' }}</span>
          <span v-if="order.person_count">{{ order.person_count }} 人</span>
          <span>收银员：{{ order.creatorName || '-' }}</span>
          <span>{{ order.create_time }}</span>
        </div>
        <div class="sum-items">
          <div class="sum-item" v-for="it in order.items || []" :key="it.id">
            <div class="si-main">
              <div class="si-name">
                {{ it.material_name }}
                <a-tag v-if="it.item_status === 'added'" color="orange">加菜</a-tag>
              </div>
              <div class="si-sub" v-if="optionsText(it.options_json)">{{ optionsText(it.options_json) }}</div>
              <div class="si-sub" v-if="it.remark">备注：{{ it.remark }}</div>
            </div>
            <div class="si-right">
              <div class="si-qty">¥{{ fmt(it.unit_price) }} × {{ it.quantity }}</div>
              <div class="si-amount">¥{{ fmt(it.all_price) }}</div>
            </div>
          </div>
        </div>
        <div class="sum-total">
          <span>合计</span>
          <span class="sum-total-num">¥{{ fmt(order.total_amount) }}</span>
        </div>
      </div>
      <!-- 右：结算面板 -->
      <div class="pay-side">
        <!-- 结算成功态 -->
        <div class="pay-result" v-if="result">
          <div class="result-icon">✓</div>
          <div class="result-title">结算成功</div>
          <div class="result-no">{{ result.orderNo }}</div>
          <div class="result-rows">
            <div class="r-row"><span>合计</span><span>¥{{ fmt(result.totalAmount) }}</span></div>
            <div class="r-row" v-if="Number(result.discountAmount) > 0"><span>会员优惠</span><span>-¥{{ fmt(result.discountAmount) }}</span></div>
            <div class="r-row" v-if="Number(result.eraseAmount) > 0"><span>抹零</span><span>-¥{{ fmt(result.eraseAmount) }}</span></div>
            <div class="r-row r-pay"><span>实收</span><span>¥{{ fmt(result.payAmount) }}</span></div>
            <div class="r-row"><span>支付方式</span><span>{{ payText(result.payType) }}</span></div>
          </div>
          <a-button type="primary" size="large" block @click="backBoard">返回桌面</a-button>
        </div>
        <!-- 结算操作态 -->
        <template v-else>
          <a-alert v-if="!settleReady" type="warning" showIcon
                   :message="'当前订单状态（' + statusText(order.status) + '）不可结算'" class="pay-alert" />
          <div class="pay-card">
            <div class="pay-sec-title">会员</div>
            <div v-if="member" class="member-box">
              <div class="member-info">
                <div class="member-name">{{ member.contacts || '会员' }}
                  <span v-if="member.levelName" class="member-level">{{ member.levelName }}</span>
                </div>
                <div class="member-phone">{{ member.telephone || '' }}</div>
              </div>
              <a-button size="small" @click="clearMember">清除</a-button>
            </div>
            <template v-else>
              <div class="member-search">
                <a-input v-model="memberKeyword" placeholder="手机号 / 姓名 / 卡号 / 会员码"
                         @pressEnter="searchMember" />
                <a-button :loading="memberSearching" @click="searchMember">查询</a-button>
              </div>
              <div class="member-results" v-if="memberResults.length">
                <div class="member-item" v-for="m in memberResults" :key="m.memberId" @click="pickMember(m)">
                  <span class="mi-name">{{ m.contacts || m.cardNo }}</span>
                  <span class="mi-phone">{{ m.telephone || '' }}</span>
                  <a-tag v-if="m.levelName" color="blue">{{ m.levelName }}</a-tag>
                </div>
              </div>
              <div class="member-tip">会员价优惠由后端结算时计算</div>
            </template>
          </div>
          <div class="pay-card">
            <div class="pay-sec-title">抹零</div>
            <div class="erase-row">
              <a-input-number v-model="eraseAmount" :min="0" :max="Number(order.total_amount) || 0"
                              :precision="2" style="width: 160px" />
              <span class="erase-tip">抹零金额不能超过合计 ¥{{ fmt(order.total_amount) }}</span>
            </div>
          </div>
          <div class="pay-card">
            <div class="pay-sec-title">支付方式</div>
            <div class="pay-types">
              <div class="pay-type" :class="{ active: payType === 'cash' }" @click="payType = 'cash'">现金</div>
              <div class="pay-type" :class="{ active: payType === 'wx' }" @click="payType = 'wx'">微信</div>
              <div class="pay-type" :class="{ active: payType === 'ali' }" @click="payType = 'ali'">支付宝</div>
            </div>
            <template v-if="payType !== 'cash'">
              <div class="pay-mode-row">
                <div class="pay-mode" :class="{ active: payMode === 'bar' }" @click="payMode = 'bar'">付款码支付（扫顾客码）</div>
                <div class="pay-mode" :class="{ active: payMode === 'qrcode' }" @click="payMode = 'qrcode'">收款码（顾客扫码）</div>
              </div>
              <!-- B扫C：扫码枪录入顾客付款码后结算（后端识别渠道并同步推进） -->
              <template v-if="payMode === 'bar'">
                <div class="auth-row">
                  <a-input v-model="authCode" placeholder="扫码枪录入顾客付款码" :disabled="!settleReady"
                           @pressEnter="submitBar" />
                  <a-button type="primary" :loading="settling" :disabled="!settleReady" @click="submitBar">确认支付</a-button>
                </div>
              </template>
              <!-- C扫B：生成收款二维码，2s 轮询支付状态至成功 -->
              <template v-else>
                <div class="qr-area" v-if="qr">
                  <div class="qr-amount">收款：¥{{ fmt(qr.payAmount) }}（以结算为准）</div>
                  <img v-if="qr.qrBase64" class="qr-img" :src="'data:image/png;base64,' + qr.qrBase64" alt="收款码" />
                  <div class="qr-tip" v-if="qrExpired">收款码已超时，请重新生成</div>
                  <div class="qr-tip" v-else-if="qrFailed">支付失败或已关闭，请重新生成收款码</div>
                  <div class="qr-tip" v-else>请顾客使用{{ payType === 'wx' ? '微信' : '支付宝' }}扫码支付，支付完成后自动结算</div>
                  <div class="qr-actions">
                    <a-button size="small" @click="createQrcode">重新生成</a-button>
                    <a-button size="small" @click="cancelQr">取消</a-button>
                  </div>
                </div>
                <a-button v-else type="primary" size="large" block :loading="settling"
                          :disabled="!settleReady" @click="createQrcode">生成收款码</a-button>
              </template>
            </template>
            <a-button v-else type="primary" size="large" block :loading="settling"
                      :disabled="!settleReady" @click="submitCash">确认结算</a-button>
          </div>
        </template>
      </div>
    </div>
    <div class="settle-loading" v-else>
      <a-spin size="large" tip="订单加载中..." />
    </div>
    <!-- 打印弹窗（结账单 JZD，autoPrint 开启时静默打印） -->
    <print-modal ref="printModal" />
  </div>
</template>

<script>
import Vue from 'vue'
import { USER_INFO } from '@/store/mutation-types'
import { storeMy, orderDetail, orderSettle, orderPayQrcode, orderPayStatus } from '@/api/plugin/dining'
import { cashierMemberSearch } from '@/api/plugin/member'
import PrintModal from './PrintModal'

const POLL_INTERVAL = 2000 // C扫B 支付状态轮询间隔
const QR_TIMEOUT_MS = 5 * 60 * 1000 // 收款码有效期（超时停止轮询）

/**
 * 结算页（/cashier/dining/settle/:orderId）：
 * 会员（memberId 传后端计算优惠）/ 抹零（eraseAmount）/ 支付方式：
 * 现金直接 settle；微信支付宝 B扫C（authCode 扫码枪录入）settle；
 * C扫B pay-qrcode 生成收款码 + pay-status 2s 轮询至成功（后端自动推进结算）。
 * 全部金额（合计/优惠/抹零后应收）均展示后端返回值，前端不计算。
 */
export default {
  name: 'DiningCashierSettle',
  components: { PrintModal },
  data() {
    return {
      userName: '',
      storeName: '',
      orderId: null,
      order: null,
      member: null,
      memberKeyword: '',
      memberResults: [],
      memberSearching: false,
      eraseAmount: 0,
      payType: 'cash',
      payMode: 'bar',
      authCode: '',
      authTimer: null,
      settling: false,
      qr: null,
      qrExpired: false,
      qrFailed: false,
      pollTimer: null,
      pollStartAt: 0,
      pollChecking: false,
      result: null
    }
  },
  computed: {
    tableLabel() {
      if (!this.order) return ''
      return this.order.order_type === 'takeout' ? '外带单' : (this.order.tableName || '堂食')
    },
    settleReady() {
      // 云贝数字状态（Task 3.2）：'1' 待付款/待接单、'2' 就餐中（餐后付）、parked 均可结算
      return !!this.order && ['1', '2', 'parked'].indexOf(this.order.status) > -1
    }
  },
  watch: {
    // 切换支付方式/模式时清理在途收款码轮询与付款码输入
    payType() {
      this.resetPayState()
    },
    payMode() {
      this.resetPayState()
    },
    // 扫码枪无回车后缀时按码长自动提交（微信 1 开头 16~19 位；支付宝 25~30 开头）
    authCode(val) {
      const code = (val || '').trim()
      clearTimeout(this.authTimer)
      if (/^1\d{15,18}$/.test(code) || /^(2[5-9]|30)\d{16,28}$/.test(code)) {
        this.authTimer = setTimeout(() => {
          if (code === (this.authCode || '').trim()) {
            this.submitBar()
          }
        }, 300)
      }
    }
  },
  created() {
    this.initUserInfo()
    this.resolveStore()
    this.orderId = Number(this.$route.params.orderId) || null
    if (!this.orderId) {
      this.$message.error('缺少订单参数')
      this.backBoard()
      return
    }
    this.loadOrder()
  },
  beforeDestroy() {
    this.stopPolling()
    clearTimeout(this.authTimer)
  },
  methods: {
    initUserInfo() {
      const info = Vue.ls.get(USER_INFO)
      this.userName = (info && (info.username || info.loginName)) || ''
    },
    resolveStore() {
      let store = null
      try {
        store = JSON.parse(sessionStorage.getItem('diningStore') || 'null')
      } catch (e) {
        store = null
      }
      if (store && store.name) {
        this.storeName = store.name
        return
      }
      storeMy().then(res => {
        if (res.code === 200 && res.data && res.data.name) {
          this.storeName = res.data.name
          sessionStorage.setItem('diningStore', JSON.stringify(res.data))
        }
      }).catch(() => {})
    },
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    statusText(s) {
      return { '1': '待付款/待接单', '2': '就餐中', '3': '已完成', '5': '已取消', '6': '申请退款',
        '7': '退款通过', '8': '退款拒绝', '9': '已拒单', parked: '挂单中' }[s] || s
    },
    statusColor(s) {
      return { '1': 'orange', '2': 'blue', '3': 'green', '5': 'red', '6': 'orange',
        '7': 'red', '8': 'default', '9': 'red', parked: 'purple' }[s] || ''
    },
    payText(t) {
      if (!t) return '-'
      return { cash: '现金', wx: '微信', ali: '支付宝' }[t] || t
    },
    optionsText(json) {
      if (!json) return ''
      try {
        const arr = JSON.parse(String(json))
        if (!Array.isArray(arr)) return ''
        return arr.map(o => o.valueName).filter(Boolean).join('/')
      } catch (e) {
        return ''
      }
    },
    loadOrder() {
      orderDetail(this.orderId).then(res => {
        if (res.code === 200 && res.data) {
          this.order = res.data
          // 订单已带会员（如挂单前已写）则回显
          if (!this.member && this.order.member_id && this.order.memberName) {
            this.member = { memberId: this.order.member_id, contacts: this.order.memberName }
          }
        } else {
          this.$message.error(res.message || '订单查询失败')
          this.backBoard()
        }
      }).catch(() => {
        this.$message.error('订单查询失败')
        this.backBoard()
      })
    },
    // ============ 会员 ============
    searchMember() {
      const kw = (this.memberKeyword || '').trim()
      if (!kw) {
        this.$message.warning('请输入手机号 / 姓名 / 卡号')
        return
      }
      this.memberSearching = true
      cashierMemberSearch(kw).then(res => {
        if (res.code === 200) {
          this.memberResults = (res.data || []).slice(0, 20)
          if (!this.memberResults.length) {
            this.$message.info('未找到会员')
          }
        } else {
          this.$message.error(res.message || '会员查询失败')
        }
      }).catch(() => {
        this.$message.error('会员查询失败，请重试')
      }).finally(() => {
        this.memberSearching = false
      })
    },
    pickMember(m) {
      this.member = {
        memberId: m.memberId,
        contacts: m.contacts || m.cardNo,
        telephone: m.telephone || '',
        levelName: m.levelName || ''
      }
      this.memberResults = []
      this.memberKeyword = ''
    },
    clearMember() {
      this.member = null
    },
    // ============ 结算 ============
    buildBody() {
      const body = { payType: this.payType }
      if (this.member && this.member.memberId) body.memberId = this.member.memberId
      body.eraseAmount = Number(this.eraseAmount) || 0
      return body
    },
    submitCash() {
      this.doSettle(this.buildBody())
    },
    submitBar() {
      if (this.settling) return
      const code = (this.authCode || '').trim()
      if (!code) {
        this.$message.warning('请扫描顾客付款码')
        return
      }
      if (!/^1\d{15,18}$/.test(code) && !/^(2[5-9]|30)\d{10,28}$/.test(code)) {
        this.$message.warning('付款码格式不正确，请确认为微信或支付宝付款码')
        return
      }
      clearTimeout(this.authTimer)
      this.authCode = ''
      this.doSettle(Object.assign(this.buildBody(), { authCode: code }))
    },
    // 现金 / B扫C 统一结算入口；金额与状态以 settle 返回为准
    doSettle(body) {
      if (!this.settleReady || this.settling) return
      this.settling = true
      this.stopPolling()
      orderSettle(this.orderId, body).then(res => {
        if (res.code === 200 && res.data) {
          this.result = res.data
          this.$message.success('结算成功')
          this.handleSettlePrint()
        } else {
          this.$message.error(res.message || '结算失败')
        }
      }).catch(() => {
        this.$message.error('结算失败，请重试')
      }).finally(() => {
        this.settling = false
      })
    },
    // C扫B：生成收款码（后端返回应收金额 payAmount）
    createQrcode() {
      if (!this.settleReady || this.settling) return
      this.settling = true
      this.stopPolling()
      const body = this.buildBody()
      orderPayQrcode(this.orderId, body).then(res => {
        if (res.code === 200 && res.data) {
          this.qr = res.data
          this.qrExpired = false
          this.qrFailed = false
          this.startPolling()
        } else {
          this.$message.error(res.message || '生成收款码失败')
        }
      }).catch(() => {
        this.$message.error('生成收款码失败，请重试')
      }).finally(() => {
        this.settling = false
      })
    },
    cancelQr() {
      this.resetPayState()
    },
    resetPayState() {
      this.stopPolling()
      clearTimeout(this.authTimer)
      this.authCode = ''
      this.qr = null
      this.qrExpired = false
      this.qrFailed = false
    },
    // ============ C扫B 轮询 ============
    startPolling() {
      this.stopPolling()
      this.pollStartAt = Date.now()
      this.pollTimer = setInterval(this.checkPayStatus, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    checkPayStatus() {
      if (this.pollChecking || !this.qr || !this.qr.payOrderNo) return
      if (Date.now() - this.pollStartAt >= QR_TIMEOUT_MS) {
        this.stopPolling()
        this.qrExpired = true
        return
      }
      this.pollChecking = true
      orderPayStatus({ payOrderNo: this.qr.payOrderNo }).then(res => {
        if (res.code !== 200 || !res.data) return
        const status = String(res.data.payStatus)
        if (status === '1') {
          // 已支付：后端已推进结算，重读订单取最终金额
          this.stopPolling()
          this.$message.success('支付成功')
          this.finishByQr()
        } else if (status === '2') {
          this.stopPolling()
          this.qrFailed = true
        }
      }).catch(() => {
        // 单次轮询异常忽略，下轮重试
      }).finally(() => {
        this.pollChecking = false
      })
    },
    finishByQr() {
      orderDetail(this.orderId).then(res => {
        if (res.code === 200 && res.data) {
          this.order = res.data
          this.result = {
            orderNo: this.order.order_no,
            totalAmount: this.order.total_amount,
            discountAmount: this.order.discount_amount,
            eraseAmount: this.order.erase_amount,
            payAmount: this.order.pay_amount,
            payType: this.order.pay_type
          }
          this.handleSettlePrint()
        }
      }).catch(() => {})
    },
    // 结算成功自动打印结账单（JZD）：autoPrint 开启→静默打印，否则弹预览；fire-and-forget 不阻塞结算提示
    async handleSettlePrint() {
      if (!this.orderId) return
      if (await PrintModal.isAutoPrintEnabled()) {
        this.$refs.printModal.printSilent(this.orderId, 'settle')
      } else {
        this.$refs.printModal.show(this.orderId, 'settle')
      }
    },
    backBoard() {
      this.$router.push('/cashier/dining')
    }
  }
}
</script>

<style scoped>
.settle-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  overflow: hidden;
}
.top-bar {
  height: 56px;
  flex-shrink: 0;
  background: #001529;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}
.top-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.top-title {
  font-size: 17px;
  font-weight: 600;
}
.top-order-no {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
}
.top-right {
  display: flex;
  align-items: center;
}
.top-store {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  margin-right: 14px;
}
.top-user {
  font-size: 13px;
}
.settle-body {
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  min-height: 0;
}
.settle-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.summary-side {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sum-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.sum-table {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}
.sum-no {
  font-size: 13px;
  color: #999;
}
.sum-meta {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}
.sum-items {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 10px 0;
}
.sum-item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 0;
}
.si-main { flex: 1; min-width: 0; }
.si-name {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}
.si-sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}
.si-right {
  text-align: right;
  flex-shrink: 0;
}
.si-qty { font-size: 12px; color: #999; }
.si-amount {
  font-size: 14px;
  color: #333;
  font-weight: 600;
  margin-top: 2px;
}
.sum-total {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  font-size: 15px;
  color: #555;
}
.sum-total-num {
  font-size: 26px;
  font-weight: 700;
  color: #e4393c;
}
.pay-side {
  width: 420px;
  flex-shrink: 0;
  overflow-y: auto;
}
.pay-alert {
  margin-bottom: 12px;
}
.pay-card {
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.pay-sec-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}
.member-search {
  display: flex;
  gap: 8px;
}
.member-results {
  margin-top: 8px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}
.member-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  cursor: pointer;
  font-size: 13px;
}
.member-item:hover { background: #f5f8ff; }
.mi-name { font-weight: 600; color: #333; }
.mi-phone { color: #999; flex: 1; }
.member-tip {
  font-size: 12px;
  color: #bbb;
  margin-top: 8px;
}
.member-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.member-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.member-level {
  font-size: 12px;
  color: #0081ff;
  margin-left: 8px;
  font-weight: 400;
}
.member-phone {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}
.erase-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.erase-tip {
  font-size: 12px;
  color: #bbb;
}
.pay-types {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}
.pay-type {
  flex: 1;
  text-align: center;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 0;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  user-select: none;
}
.pay-type.active {
  background: #0081ff;
  border-color: #0081ff;
  color: #fff;
  font-weight: 600;
}
.pay-mode-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.pay-mode {
  flex: 1;
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 7px 0;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  user-select: none;
}
.pay-mode.active {
  border: 1px solid #0081ff;
  color: #0081ff;
  background: #f0f8ff;
  font-weight: 600;
}
.auth-row {
  display: flex;
  gap: 8px;
}
.qr-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0 8px;
}
.qr-amount {
  font-size: 13px;
  color: #555;
  margin-bottom: 10px;
}
.qr-img {
  width: 220px;
  height: 220px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}
.qr-tip {
  margin-top: 10px;
  color: #555;
  font-size: 13px;
  text-align: center;
}
.qr-actions {
  margin-top: 12px;
  display: flex;
  gap: 12px;
}
.pay-result {
  background: #fff;
  border-radius: 8px;
  padding: 28px 24px;
  text-align: center;
}
.result-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #52c41a;
  color: #fff;
  font-size: 34px;
  line-height: 64px;
  margin: 0 auto;
}
.result-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-top: 14px;
}
.result-no {
  font-size: 13px;
  color: #999;
  margin-top: 6px;
}
.result-rows {
  margin: 18px 0 22px;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}
.r-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #555;
  padding: 5px 4px;
}
.r-pay {
  color: #e4393c;
  font-size: 18px;
  font-weight: 700;
}
</style>
